/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag
 */
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import CrossIcon from '@atlaskit/icon/core/cross';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import { Box, Flex, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	footer: {
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.200'),
		boxShadow: token('elevation.shadow.overflow'),
		backgroundColor: token('elevation.surface'),
		position: 'sticky',
		bottom: 0,
		width: '100%',
		textAlign: 'left',
		zIndex: 1,
	},
	content: {
		flex: 1,
	},
});

const messages = defineMessages({
	footerMessage: {
		id: 'ai-audio.playlist.footer-message',
		defaultMessage: 'Some items are no longer available due to permissions or deletion.',
		description: 'Message for the footer of the playlist',
	},
	closeLabel: {
		id: 'ai-audio.playlist.footer-close',
		defaultMessage: 'Close',
		description: 'Label for the close button in the footer',
	},
});

interface FooterMessageProps {
	onClose?: () => void;
}

export const FooterMessage = ({ onClose }: FooterMessageProps) => {
	const { formatMessage } = useIntl();

	return (
		<Box xcss={styles.footer}>
			<Flex gap="space.200" alignItems="center" justifyContent="space-between">
				<StatusErrorIcon color={token('color.icon.subtle')} label="Warning" />
				<Box xcss={styles.content}>
					<Text size="small" color="color.text.subtle">
						<FormattedMessage {...messages.footerMessage} />
					</Text>
				</Box>
				{onClose && (
					<IconButton
						icon={CrossIcon}
						label={formatMessage(messages.closeLabel)}
						onClick={onClose}
						appearance="subtle"
						shape="circle"
					/>
				)}
			</Flex>
		</Box>
	);
};
